import {
  canBookHardSkills,
  canBookScreening,
  CandidateRole,
  CandidateRoleCallStatus,
  CandidateRoleScreeningStatus,
  CandidateRoleTestStatus,
  formatScheduledSlot,
  getRoleOutcome,
  getRoleStepsDone,
  getSentInterviewLink,
  getSentScreeningLink,
  hasBookedInterview,
  hasVerificationOutcome,
  isRolePaused,
  isRoleVerified,
  RoleOutcome,
  RoleStepKey,
  RoleStepsDone,
} from '@internal/components/app/roles';
import { isInterviewCompleted } from '@internal/components/app/roles/role-types';

import { RoleStep, RoleStepAction, RoleStepStatus } from './roles-hub.types';

export interface RoleStepHandlers {
  onExploreRoles: () => void;
  startingTestRoleId: string | null;
  bookingRoleId: string | null;
  actionsLocked: boolean;
  onTakeTest: () => void;
  onStartCall: () => void;
  onBookScreening: () => void;
  onBookInterview: () => void;
}

const RETAKE_COOLDOWN_HINT =
  'Tests can be retaken once every 3 months — we’ll unlock this one when the window opens.';

export const TEST_STEP_DESCRIPTION =
  '15 minutes, mostly multiple choice, only your stack. No algorithm puzzles.';

export const CALL_STEP_DESCRIPTION =
  'A 20-minute AI call about your experience.';

const PROFILE_REVIEW_BODY =
  "We'll review your profile and get back to you about the next steps.";

const SKIPPED_BY_RECRUITER_BODY =
  'Skipped by recruiter to fast track your application';

const skippedByRecruiter = (
  base: Pick<RoleStep, 'key' | 'title'>
): RoleStep => ({
  ...base,
  status: RoleStepStatus.Skipped,
});

const withSkipHints = (steps: RoleStep[], role: CandidateRole): RoleStep[] =>
  isRoleVerified(role)
    ? steps.map(step =>
        step.status === RoleStepStatus.Skipped
          ? { ...step, statusHint: SKIPPED_BY_RECRUITER_BODY }
          : step
      )
    : steps;

const VERIFIED_FALLBACK_BODY =
  'Your role is verified. Completed checks are saved for future applications.';

export const hasPendingBookingLink = (role: CandidateRole): boolean =>
  !isRolePaused(role) &&
  ((!hasBookedInterview(role) &&
    (role.interviewBooking.canRequestBookingLink ||
      Boolean(getSentInterviewLink(role)))) ||
    Boolean(getSentScreeningLink(role)));

const isBusy = (handlers: RoleStepHandlers): boolean =>
  handlers.actionsLocked ||
  Boolean(handlers.startingTestRoleId || handlers.bookingRoleId);

const PAUSED_LABEL = 'Paused';

const actionable = (
  handlers: RoleStepHandlers,
  status: RoleStepStatus,
  statusLabel?: string
): Pick<RoleStep, 'status' | 'statusLabel'> =>
  handlers.actionsLocked
    ? { status: RoleStepStatus.Pending, statusLabel: PAUSED_LABEL }
    : { status, statusLabel };

const waitingOnTeam = (
  base: Pick<RoleStep, 'key' | 'title'>,
  handlers: RoleStepHandlers,
  status: RoleStepStatus.Pending | RoleStepStatus.AwaitingReview,
  description: string
): RoleStep =>
  handlers.actionsLocked
    ? { ...base, status: RoleStepStatus.Locked }
    : { ...base, status, description };

const bookAction = (
  role: CandidateRole,
  handlers: RoleStepHandlers,
  onClick: () => void,
  testId: string
): RoleStepAction => ({
  label: handlers.bookingRoleId === role.id ? 'Opening' : 'Book a call',
  onClick,
  disabled: isBusy(handlers),
  testId,
  primary: true,
});

const scheduled = (
  base: Pick<RoleStep, 'key' | 'title'>,
  bookedAt: string | null
): RoleStep => ({
  ...base,
  status: RoleStepStatus.Scheduled,
  description: bookedAt
    ? `Meeting you on ${formatScheduledSlot(bookedAt)}`
    : undefined,
});

const buildTestStep = (
  role: CandidateRole,
  done: RoleStepsDone,
  handlers: RoleStepHandlers
): RoleStep => {
  const { status, allowRetake } = role.assessment.test;
  const base = { key: RoleStepKey.Test, title: 'Pass a skill test' };

  if (done[RoleStepKey.Test]) {
    if (status === CandidateRoleTestStatus.Skipped) {
      return skippedByRecruiter(base);
    }
    const skipped =
      status !== CandidateRoleTestStatus.Completed &&
      hasVerificationOutcome(role);
    return {
      ...base,
      status: skipped ? RoleStepStatus.Skipped : RoleStepStatus.Done,
    };
  }

  if (status === CandidateRoleTestStatus.Failed && !allowRetake) {
    return {
      ...base,
      status: RoleStepStatus.Pending,
      statusLabel: 'Retake later',
      statusHint: RETAKE_COOLDOWN_HINT,
    };
  }

  let label = 'Go to test';
  let statusLabel: string | undefined;
  if (status === CandidateRoleTestStatus.InProgress) {
    label = 'Resume test';
    statusLabel = 'In progress';
  } else if (status === CandidateRoleTestStatus.Failed) {
    label = 'Retake test';
    statusLabel = 'Retake pending';
  }

  return {
    ...base,
    description: TEST_STEP_DESCRIPTION,
    ...actionable(
      handlers,
      statusLabel ? RoleStepStatus.Pending : RoleStepStatus.Available,
      statusLabel
    ),
    action: {
      label: handlers.startingTestRoleId === role.id ? 'Preparing' : label,
      onClick: handlers.onTakeTest,
      disabled: isBusy(handlers) || !role.pickable,
      testId: 'role-step-take-test',
    },
  };
};

const buildCallStep = (
  role: CandidateRole,
  done: RoleStepsDone,
  handlers: RoleStepHandlers
): RoleStep => {
  const { status, allowRetake, errored } = role.assessment.call;
  const base = { key: RoleStepKey.Call, title: 'Talk with Mark' };

  if (done[RoleStepKey.Call]) {
    if (status === CandidateRoleCallStatus.Skipped) {
      return skippedByRecruiter(base);
    }
    const skipped =
      status !== CandidateRoleCallStatus.Completed &&
      hasVerificationOutcome(role);
    return {
      ...base,
      status: skipped ? RoleStepStatus.Skipped : RoleStepStatus.Done,
    };
  }

  const canRetake = status === CandidateRoleCallStatus.Completed && allowRetake;

  let callStatus = RoleStepStatus.Available;
  let statusLabel: string | undefined;
  if (errored) {
    callStatus = RoleStepStatus.Error;
  } else if (canRetake) {
    callStatus = RoleStepStatus.Pending;
    statusLabel = 'Retake available';
  }

  return {
    ...base,
    ...actionable(handlers, callStatus, statusLabel),
    description: errored
      ? "Your last call didn't complete. Start it again to continue."
      : CALL_STEP_DESCRIPTION,
    action: {
      label: canRetake ? 'Retake call' : 'Go to call',
      onClick: handlers.onStartCall,
      disabled: isBusy(handlers),
      testId: 'role-step-start-call',
    },
  };
};

const buildReviewStep = (
  done: RoleStepsDone,
  handlers: RoleStepHandlers
): RoleStep => {
  const base = { key: RoleStepKey.Review, title: 'Recruiter review' };

  if (done[RoleStepKey.Review]) {
    return { ...base, status: RoleStepStatus.Done };
  }

  if (done[RoleStepKey.Test] && done[RoleStepKey.Call]) {
    return waitingOnTeam(
      base,
      handlers,
      RoleStepStatus.Pending,
      PROFILE_REVIEW_BODY
    );
  }

  return {
    ...base,
    status: RoleStepStatus.Locked,
    description: PROFILE_REVIEW_BODY,
  };
};

const buildRecruiterStep = (
  role: CandidateRole,
  done: RoleStepsDone,
  handlers: RoleStepHandlers
): RoleStep => {
  const screening = role.assessment.screening;
  const base = {
    key: RoleStepKey.Recruiter,
    title: 'Interview with recruiter',
  };

  if (done[RoleStepKey.Recruiter]) {
    if (screening.status === CandidateRoleScreeningStatus.Skipped) {
      return skippedByRecruiter(base);
    }
    // A final role decision closes the step. A booking alone is no evidence
    // that the interview happened, so only a completed screening counts.
    const skipped =
      hasVerificationOutcome(role) &&
      screening.status !== CandidateRoleScreeningStatus.Completed;
    return {
      ...base,
      status: skipped ? RoleStepStatus.Skipped : RoleStepStatus.Done,
    };
  }

  if (!done[RoleStepKey.Review]) {
    return {
      ...base,
      status: RoleStepStatus.Locked,
      description: 'Booking unlocks after a successful recruiter review.',
    };
  }

  if (screening.status === CandidateRoleScreeningStatus.Booked) {
    return scheduled(base, screening.bookedAt);
  }

  if (canBookScreening(role)) {
    return {
      ...base,
      ...actionable(handlers, RoleStepStatus.Available),
      description:
        'Your profile review was successful. Book a call with your recruiter.',
      action: bookAction(
        role,
        handlers,
        handlers.onBookScreening,
        'role-step-book-screening'
      ),
    };
  }

  return waitingOnTeam(
    base,
    handlers,
    RoleStepStatus.Pending,
    "We'll send you a booking link for a call with a recruiter soon."
  );
};

const buildTechnicalStep = (
  role: CandidateRole,
  done: RoleStepsDone,
  handlers: RoleStepHandlers
): RoleStep => {
  const base = { key: RoleStepKey.Technical, title: 'Technical interview' };

  if (done[RoleStepKey.Technical]) {
    const skipped = hasVerificationOutcome(role) && !isInterviewCompleted(role);
    return {
      ...base,
      status: skipped ? RoleStepStatus.Skipped : RoleStepStatus.Done,
    };
  }

  if (!done[RoleStepKey.Recruiter]) {
    return {
      ...base,
      status: RoleStepStatus.Locked,
      description:
        'Booking unlocks after a successful interview with recruiter.',
    };
  }

  if (hasBookedInterview(role)) {
    return scheduled(base, role.assessment.interview.bookedAt);
  }

  if (canBookHardSkills(role)) {
    return {
      ...base,
      ...actionable(handlers, RoleStepStatus.Available),
      description:
        'Book a conversation with a Lemon.io developer, up to 90 minutes.',
      action: bookAction(
        role,
        handlers,
        handlers.onBookInterview,
        'role-step-book-interview'
      ),
    };
  }

  return waitingOnTeam(
    base,
    handlers,
    RoleStepStatus.Pending,
    "We'll send you a booking link for the technical interview soon."
  );
};

const buildVerificationStep = (
  role: CandidateRole,
  done: RoleStepsDone,
  handlers: RoleStepHandlers
): RoleStep => {
  const base = { key: RoleStepKey.Verification, title: 'Role verification' };

  if (isRoleVerified(role)) {
    const seniority = role.verification.seniority;
    return {
      ...base,
      status: RoleStepStatus.Done,
      description: seniority
        ? `Your level for this role: ${seniority}. You can apply to ${seniority} projects for this role.`
        : VERIFIED_FALLBACK_BODY,
    };
  }

  if (done[RoleStepKey.Verification]) {
    return { ...base, status: RoleStepStatus.Done };
  }

  if (!done[RoleStepKey.Technical]) {
    return {
      ...base,
      status: RoleStepStatus.Locked,
      description:
        'Once you’re verified for this role, you can apply for projects that match it.',
    };
  }

  return waitingOnTeam(
    base,
    handlers,
    RoleStepStatus.AwaitingReview,
    'Your recruiter will confirm the role result. No action needed now.'
  );
};

const applyOutcome = (
  steps: RoleStep[],
  done: RoleStepsDone,
  outcome: RoleOutcome,
  handlers: RoleStepHandlers
): RoleStep[] =>
  steps.map(step => {
    if (step.key === outcome.stepKey) {
      return {
        ...step,
        description: outcome.message,
        action: handlers.actionsLocked
          ? undefined
          : {
              label: outcome.ctaLabel,
              onClick: handlers.onExploreRoles,
              testId: 'role-step-explore-roles',
            },
      };
    }
    return done[step.key]
      ? step
      : { key: step.key, title: step.title, status: RoleStepStatus.Locked };
  });

export const buildRoleSteps = (
  role: CandidateRole,
  handlers: RoleStepHandlers
): RoleStep[] => {
  const done = getRoleStepsDone(role);

  const steps = [
    buildTestStep(role, done, handlers),
    buildCallStep(role, done, handlers),
    buildReviewStep(done, handlers),
    buildRecruiterStep(role, done, handlers),
    buildTechnicalStep(role, done, handlers),
    buildVerificationStep(role, done, handlers),
  ];

  const outcome = getRoleOutcome(role);
  return withSkipHints(
    outcome ? applyOutcome(steps, done, outcome, handlers) : steps,
    role
  );
};
